import { DateLabelComponent } from '@/app/components/shared/date-label/date-label.component';
import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonMatchesListComponent } from '@/app/components/shared/skeleton';
import { useWsMatchMatches } from '@/app/core/websocket/hooks';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { isSameDay, isToday } from 'date-fns';
import { MatchApi } from '../../../home/models';
import { CompetitionByTeamRowComponent } from '../../components';
import { TeamMatchesActiveService } from '../../services';

@Component({
  selector: 'app-matches',
  standalone: true,
  imports: [
    TranslateModule,
    NoResultComponent,
    SkeletonMatchesListComponent,
    DateLabelComponent,
    CompetitionByTeamRowComponent,
  ],
  templateUrl: './matches.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchesComponent {
  private wsMatches = useWsMatchMatches();
  private route = inject(ActivatedRoute);
  private teamMatchesActiveService = inject(TeamMatchesActiveService);
  private observer!: IntersectionObserver;

  teamId = signal<string>('');

  // wsMatchesData = signal<Record<string, IWsMatch>>({});

  fetchComputed = computed(() => ({
    isLoading:
      this.teamMatchesActiveService.isLoadingTeamsMatchActiveFavorites(),
    isError: this.teamMatchesActiveService.isErrorTeamsMatchActiveFavorites(),
  }));

  teamMatchesActive = computed(() =>
    this.teamMatchesActiveService.teamsMatchActive()
  );

  wsDataMatch = effect(
    () => {
      this.teamMatchesActiveService.wsMatchesData.set(this.wsMatches.data());
    },
    { allowSignalWrites: true }
  );

  scoreContainerRef = viewChild<ElementRef<HTMLDivElement>>(
    'soccerMatchesContainer'
  );
  scrollTriggerRef = viewChild<ElementRef<HTMLDivElement>>('scrollTrigger');

  ngAfterViewInit() {
    const refItem = this.scrollTriggerRef()?.nativeElement;

    if (!refItem) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            this.teamMatchesActiveService.isMoreMatches() &&
            this.teamMatchesActiveService.teamsMatchActive() !== null &&
            this.teamMatchesActiveService.paginationMatches() >= 1
          ) {
            this.teamMatchesActiveService.getTeamMatchesActive(
              this.teamId(),
              this.teamMatchesActiveService.paginationMatches() + 1
            );
          }
        });
      },
      {
        root: null, // null = viewport
        rootMargin: '200px', // Se activará 200px antes de que el elemento sea visible
        threshold: 0.01,
      }
    );

    this.observer.observe(refItem);

    if (!this.teamMatchesActiveService.isLoadingTeamsMatchActiveFavorites()) {
      this.teamMatchesActiveService.getTeamMatchesActive(this.teamId());
    }

    // 👉 Primer fetch con skeleton
    if (!this.teamMatchesActiveService.isLoadingTeamsMatchActiveFavorites()) {
      this.teamMatchesActiveService.getTeamMatchesActive(this.teamId());
    }
  }

  isNextMatches(date: Date) {
    return !isToday(date);
  }

  isFirstMatchOfDay(matches: MatchApi[], index: number): boolean {
    if (index === 0) return true; // El primer partido siempre muestra la fecha
    const current = matches[index].Date;
    const previous = matches[index - 1].Date;
    return !isSameDay(current, previous);
  }

  constructor() {
    const teamId = this.route.snapshot.paramMap.get('teamId');
    if (teamId) {
      this.teamId.set(teamId);
    }
  }

  ngOnDestroy() {
    this.teamMatchesActiveService.teamsMatchActive.set(null);
    this.teamMatchesActiveService.teamsMatchPagination.set(null);
    this.teamMatchesActiveService.wsMatchesData.set({});

    this.wsMatches.disconnect();
  }
}
